import { type ChangeEvent, useCallback } from 'react';
import type { Lens } from '@hookform/lenses';
import { useFormContext, useWatch } from 'react-hook-form';
import { z } from 'zod/v4';
import { USD_CURRENCY_MASK } from '@interxfi/design-system/components/input/text-input/masks';
import BoltIcon from '@interxfi/design-system/icons/react/icon-bolt';
import type { IxcPortfolioV1InstantDepositControls, IxcPortfolioV1InstantDepositControlsLimits } from '@interxfi/types/generated/api';
import { parseNumber } from '@interxfi/utilities/number/parse';
import { AccordionFormGroup } from '../../../../../../components/card-group/form/accordion-form-group';
import { AppSwitchInputWithLens } from '../../../../../../components/form/app-switch-input';
import { AppTextInputWithLens } from '../../../../../../components/form/app-text-input';
import { addDefinedValues } from '../../../../../../util/has-value';
import {
  ALLOW_EMPTY_STRING_OR_EMPTY_USD_VALUE,
  DEFAULT_USD_CURRENCY_VALIDATION_DISPLAY_FORMATTER,
  getNumericStringValidator,
} from '../../../../../../util/validation';

export const programControlsInstantDepositFormSchema = z.object({
  canUseAdvance: z.object({
    value: z.boolean(),
    limits: z.object({
      max: getNumericStringValidator('Max Advance per Account', {
        required: ALLOW_EMPTY_STRING_OR_EMPTY_USD_VALUE,
        gte: 0,
        displayFormatter: DEFAULT_USD_CURRENCY_VALIDATION_DISPLAY_FORMATTER,
      }),
    }),
  }),
});

export type ProgramControlsInstantDepositFormValues = z.infer<typeof programControlsInstantDepositFormSchema>;

export const programControlsInstantDepositFormDefaultValues: ProgramControlsInstantDepositFormValues = {
  canUseAdvance: {
    value: false,
    limits: {
      max: '',
    },
  },
};

export function getInitialFormValuesForProgramInstantDepositControls(
  data: IxcPortfolioV1InstantDepositControls | undefined,
): ProgramControlsInstantDepositFormValues {
  if (!data) {
    return programControlsInstantDepositFormDefaultValues;
  }

  return {
    canUseAdvance: {
      value: Boolean(data.settings?.canUseAdvance),
      limits: {
        max: data.limits?.maxAdvancePerAccount?.toString() || '',
      },
    },
  };
}

export function formValuesToProgramInstantDepositControls(values: ProgramControlsInstantDepositFormValues): IxcPortfolioV1InstantDepositControls {
  const limits = addDefinedValues<IxcPortfolioV1InstantDepositControlsLimits>(
    {},
    {
      maxAdvancePerAccount: parseNumber(values.canUseAdvance.limits.max) || undefined,
    },
  );

  return addDefinedValues<IxcPortfolioV1InstantDepositControls>(
    {
      settings: {
        canUseAdvance: values.canUseAdvance.value,
      },
    },
    {
      limits: Object.keys(limits).length > 0 ? limits : undefined,
    },
  );
}

interface ProgramInstantDepositControlsFormProps {
  lens: Lens<ProgramControlsInstantDepositFormValues>;
}

export function ProgramInstantDepositControlsForm({ lens }: ProgramInstantDepositControlsFormProps) {
  const { setValue } = useFormContext();
  const canUseAdvance = useWatch(lens.focus('canUseAdvance.value').interop());
  const { name: limitsName } = lens.focus('canUseAdvance.limits').interop();
  const handleToggle = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!e.target.checked) {
        setValue(limitsName, { ...programControlsInstantDepositFormDefaultValues.canUseAdvance.limits }, { shouldValidate: true });
      }
    },
    [limitsName, setValue],
  );

  return (
    <AccordionFormGroup icon={<BoltIcon aria-hidden />} title="Instant Deposit">
      <AccordionFormGroup.Section
        toggleInputElement={
          <AppSwitchInputWithLens align="space-between" label="Allow Advances" lens={lens.focus('canUseAdvance.value')} onChange={handleToggle} />
        }
      >
        {canUseAdvance ? (
          <AppTextInputWithLens label="Max Advance per Account" lens={lens.focus('canUseAdvance.limits.max')} maskOptions={USD_CURRENCY_MASK} />
        ) : null}
      </AccordionFormGroup.Section>
    </AccordionFormGroup>
  );
}
